<template>
  <div class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0f18] overflow-hidden font-sans">
    <canvas ref="canvasRef" class="absolute inset-0 z-0"></canvas>

    <div class="relative z-10 w-full max-w-sm bg-white p-8 rounded-xl shadow-2xl mx-4">
      <div class="text-center mb-8">
        <h2 class="text-lg font-extrabold text-gray-800">Global Meridian Cycle</h2>
        <p class="text-xs text-gray-500 mt-2 font-semibold">글로벌 블록체인 네트워크 로그인</p>
      </div>
      
      <form @submit.prevent="handleLogin" class="space-y-4">
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">이메일 (아이디)</label>
          <input 
            type="email" 
            v-model="email" 
            required 
            class="w-full px-4 py-3 bg-[#f0f4f8] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-colors"
            placeholder="이메일 주소 입력"
          />
        </div>

        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">비밀번호</label>
          <div class="relative flex items-center">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              v-model="password" 
              required 
              class="w-full px-4 py-3 bg-[#f0f4f8] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-16 transition-colors"
              placeholder="비밀번호 입력"
            />
            <button 
              type="button" 
              @click="showPassword = !showPassword" 
              class="absolute right-0 h-full px-4 text-xs font-bold text-gray-500 hover:text-gray-800 bg-gray-200/50 rounded-r-lg border-l border-gray-200 transition-colors"
            >
              {{ showPassword ? '숨기기' : '보기' }}
            </button>
          </div>
        </div>

        <p v-if="errorMessage" class="text-red-500 text-xs text-center font-bold">{{ errorMessage }}</p>

        <button 
          type="submit" 
          class="w-full bg-[#1e2532] text-white font-bold py-3 px-4 rounded-lg hover:bg-black transition-colors text-sm mt-2 shadow-md"
        >
          로그인
        </button>
      </form>

      <div class="mt-6 flex justify-center space-x-4 text-xs font-bold text-blue-600">
        <router-link to="/signup" class="hover:text-blue-800 transition-colors">회원가입</router-link>
        <span class="text-gray-300">|</span>
        <button type="button" @click="showResetModal = true" class="hover:text-blue-800 transition-colors">비밀번호 찾기</button>
      </div>
    </div>

    <div v-if="showResetModal" class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70 px-4">
      <div class="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative transform transition-all">
        <button @click="closeResetModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-800">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        
        <h3 class="text-lg font-extrabold text-gray-800 mb-2">비밀번호 찾기</h3>
        <p class="text-xs text-gray-500 mb-6 font-semibold">가입 시 등록한 이메일 주소를 입력하시면<br/>비밀번호 재설정 링크를 보내드립니다.</p>
        
        <form @submit.prevent="handlePasswordReset" class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">가입한 이메일</label>
            <input 
              type="email" 
              v-model="resetEmail" 
              required 
              class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
              placeholder="example@email.com" 
            />
          </div>
          
          <p v-if="resetError" class="text-red-500 text-xs font-bold text-center">{{ resetError }}</p>
          <p v-if="resetSuccessMessage" class="text-green-600 text-xs font-bold text-center leading-relaxed">{{ resetSuccessMessage }}</p>
          
          <div class="flex space-x-2 pt-2 mt-4">
            <button type="button" @click="closeResetModal" class="flex-1 bg-gray-200 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-300 transition-colors">
              닫기
            </button>
            <button type="submit" :disabled="isResetting" class="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 shadow-md transition-colors disabled:opacity-50">
              {{ isResetting ? '발송 중...' : '링크 발송' }}
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import router from '../router';
import { auth, db } from '../firebase';
// [추가됨] 비밀번호 재설정 이메일 발송 함수 임포트
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export default {
  name: 'LoginView',
  setup() {
    const email = ref('');
    const password = ref('');
    const showPassword = ref(false);
    const errorMessage = ref('');
    const canvasRef = ref(null);

    // === [비밀번호 찾기 모달 상태 변수] ===
    const showResetModal = ref(false);
    const resetEmail = ref('');
    const resetError = ref('');
    const resetSuccessMessage = ref('');
    const isResetting = ref(false);

    let ctx = null;
    let animationFrameId = null;
    const particles = [];
    const particleCount = 80;

    const handleLogin = async () => {
      try {
        errorMessage.value = '';
        const userCredential = await signInWithEmailAndPassword(auth, email.value, password.value);
        const user = userCredential.user;

        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          
          // 🛑 [추가] 정지된 회원 차단 로직
          if (data.isActive === false) {
            alert('관리자에 의해 이용이 정지된 계정입니다. 고객센터에 문의해주세요.');
            await auth.signOut(); // 강제 로그아웃
            return;
          }

          if (data.role === 'admin') {
            router.push('/admin');
          } else {
            router.push('/');
          }
        }
      } catch (error) {
        errorMessage.value = '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.';
      }
    };

    // === [비밀번호 찾기 로직] ===
    const handlePasswordReset = async () => {
      if (!resetEmail.value) {
        resetError.value = '이메일을 입력해주세요.';
        return;
      }
      
      isResetting.value = true;
      resetError.value = '';
      resetSuccessMessage.value = '';

      try {
        await sendPasswordResetEmail(auth, resetEmail.value);
        resetSuccessMessage.value = '비밀번호 재설정 이메일이 발송되었습니다.\n메일함(스팸함 포함)을 확인해주세요.';
        resetEmail.value = ''; // 전송 성공 시 입력창 초기화
      } catch (error) {
        console.error("비밀번호 재설정 에러:", error);
        if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-email') {
          resetError.value = '등록되지 않거나 유효하지 않은 이메일입니다.';
        } else {
          resetError.value = '이메일 발송 중 오류가 발생했습니다. 다시 시도해주세요.';
        }
      } finally {
        isResetting.value = false;
      }
    };

    // 비밀번호 찾기 모달 닫기 및 상태 초기화
    const closeResetModal = () => {
      showResetModal.value = false;
      resetEmail.value = '';
      resetError.value = '';
      resetSuccessMessage.value = '';
    };

    const initCanvas = () => {
      if (!canvasRef.value) return;
      ctx = canvasRef.value.getContext('2d');
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvasRef.value.width,
          y: Math.random() * canvasRef.value.height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          radius: Math.random() * 1.5 + 0.5
        });
      }
      animate();
    };

    const resizeCanvas = () => {
      if (!canvasRef.value) return;
      canvasRef.value.width = window.innerWidth;
      canvasRef.value.height = window.innerHeight;
    };

    const animate = () => {
      if (!canvasRef.value || !ctx) return;
      ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
      
      for (let i = 0; i < particles.length; i++) {
        let p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0 || p.x > canvasRef.value.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvasRef.value.height) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 204, 0, 0.7)';
        ctx.fill();
        
        for (let j = i + 1; j < particles.length; j++) {
          let p2 = particles[j];
          let dx = p.x - p2.x;
          let dy = p.y - p2.y;
          let dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(255, 204, 0, ${1 - dist / 120})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    onMounted(() => {
      initCanvas();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    });

    return {
      email,
      password,
      showPassword,
      errorMessage,
      handleLogin,
      canvasRef,
      // [추가됨] 반환할 상태 및 함수
      showResetModal,
      resetEmail,
      resetError,
      resetSuccessMessage,
      isResetting,
      handlePasswordReset,
      closeResetModal
    };
  }
}
</script>